import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { checkPasswordPwned } from "../../../../services/hibpCheck";

export interface PasswordRule {
  id: string;
  /** Static fallback label. Display labels resolve via the
   *  `password.rules.{id}` i18n key first, then fall back to this. */
  label: string;
  test: (password: string) => boolean;
}

interface PasswordRuleChecklistProps {
  /** The password currently typed by the user. */
  password: string;
  /** Rule set — defaults to the canonical 4-rule set used across all
   *  password-creation pages (Complete-Signup / Reset-Password / Accept-Invite). */
  rules?: readonly PasswordRule[];
  /** Optional id for `aria-describedby` from the parent password field. */
  id?: string;
  /** When true (default), run a HIBP k-anonymity check on the password
   *  after a 600ms debounce. If the password is known-breached the
   *  rendered warning is advisory — the form is not blocked from submit. */
  hibpCheck?: boolean;
}

/** Canonical password rules — single source of truth so the live checklist
 *  UI and submit validators never drift apart. Kept at the existing 10-char
 *  minimum to match the live backend policy. */
export const PWD_RULES: readonly PasswordRule[] = [
  { id: "length", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { id: "upper",  label: "One uppercase letter",   test: (p) => /[A-Z]/.test(p) },
  { id: "lower",  label: "One lowercase letter",   test: (p) => /[a-z]/.test(p) },
  { id: "number", label: "One number",              test: (p) => /[0-9]/.test(p) },
] as const;

/**
 * PasswordRuleChecklist — live status of every password rule.
 *
 * Each rule transitions independently between "not met" (○ + muted color)
 * and "met" (✓ + confidence-green). The container is `aria-live="polite"`
 * but announcements are throttled to once per state-transition per rule
 * to avoid SR-spam on every keystroke (research §6.3).
 */
export function PasswordRuleChecklist({
  password,
  rules = PWD_RULES,
  id,
  hibpCheck = true,
}: PasswordRuleChecklistProps) {
  const { t } = useTranslation();
  const ruleResults = useMemo(
    () =>
      rules.map((r) => {
        // Try the i18n catalogue first (`password.rules.{id}`); fall back
        // to the constant label so any unknown rule still renders.
        const key = `password.rules.${r.id}`;
        const translated = t(key, { defaultValue: r.label });
        return { ...r, label: translated, met: r.test(password) };
      }),
    [password, rules, t],
  );

  // HIBP compromised-password lookup. Debounced to 600ms so we don't
  // call the API on every keystroke; aborted on unmount or when the
  // password changes. The result is advisory only — the form is not
  // blocked from submission even if a match is found.
  const [hibp, setHibp] = useState<{ pwned: boolean; count: number } | null>(null);
  const allRulesPass = ruleResults.every((r) => r.met);
  useEffect(() => {
    if (!hibpCheck || !allRulesPass) {
      setHibp(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const result = await checkPasswordPwned(password);
      if (!cancelled) setHibp(result);
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [password, hibpCheck, allRulesPass]);

  // Polite announcement throttled to "newly met" rules — fires once when a
  // rule transitions from not-met → met. Prevents spam on every keystroke.
  const prevMet = useRef<Set<string>>(new Set());
  const [announce, setAnnounce] = useState("");

  // 150ms debounce — if multiple rules transition met within a single
  // rapid-typing burst, batch them into one SR announcement to avoid
  // polite-region flood (WCAG 2.2 aria-live spam guidance).
  useEffect(() => {
    const currentMet = new Set(ruleResults.filter((r) => r.met).map((r) => r.id));
    const newlyMet: string[] = [];
    currentMet.forEach((id) => {
      if (!prevMet.current.has(id)) newlyMet.push(id);
    });
    prevMet.current = currentMet;
    if (newlyMet.length === 0) return;
    const id = window.setTimeout(() => {
      const labels = newlyMet
        .map((id) => ruleResults.find((r) => r.id === id)?.label)
        .filter(Boolean);
      // Use "and" for the last item so the announcement reads naturally:
      // "Met: One uppercase letter and One number" rather than
      // "Met: One uppercase letter, One number".
      const joined =
        labels.length <= 1
          ? (labels[0] ?? "")
          : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
      setAnnounce(`Met: ${joined}`);
    }, 150);
    return () => window.clearTimeout(id);
  }, [ruleResults]);

  return (
    <>
      <ul
        id={id}
        className="mt-2 space-y-1"
        aria-label={t("password.rulesAriaLabel")}
        role="list"
      >
        {ruleResults.map((rule) => (
          <li
            key={rule.id}
            className="flex items-center gap-2 text-xs"
            style={{ lineHeight: 1.4 }}
          >
            <span
              aria-hidden="true"
              className="inline-flex w-3 h-3 items-center justify-center font-mono text-[10px]"
              style={{ color: rule.met ? "var(--confidence)" : "var(--muted-foreground)" }}
            >
              {rule.met ? "✓" : "○"}
            </span>
            <span style={{ color: rule.met ? "var(--foreground)" : "var(--muted-foreground)" }}>
              {rule.label}
            </span>
            <span className="sr-only">{rule.met ? t("password.srMet") : t("password.srNotMet")}</span>
          </li>
        ))}
      </ul>
      {/* HIBP advisory warning — appears only when all canonical rules
          have been met and the breached-password check returns true.
          Advisory only: the form is not blocked from submitting. */}
      {hibp?.pwned && (
        <div
          className="mt-2 px-3 py-2 text-xs border"
          style={{
            borderColor: "var(--review)",
            background: "rgba(var(--review-rgb), 0.08)",
            color: "var(--foreground)",
            lineHeight: 1.5,
          }}
          role="status"
          aria-live="polite"
        >
          <span style={{ fontWeight: 600 }}>{t("password.pwnedAdvisory.title")}</span>{" "}
          {t("password.pwnedAdvisory.body")}
        </div>
      )}
      {/* SR-only live-region. The visual list above shows full state at all
          times; this region only announces transitions to avoid SR-spam. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {announce}
      </p>
    </>
  );
}

/** True iff every rule in `rules` is satisfied by `password`. */
export function allRulesMet(password: string, rules: readonly PasswordRule[] = PWD_RULES) {
  return rules.every((r) => r.test(password));
}
